interface ProjectKeysAllParmas {
  projectId: string;
}

function sanitize<T>(key: T[]): NonNullable<T>[] {
  return key.filter(
    (part): part is NonNullable<T> => part !== undefined && part !== null,
  );
}

// 2. Safe Object Sanitizer
function withFilter<TParams extends Record<string, unknown>>(
  key: unknown[],
  params?: TParams,
) {
  if (!params) return key;

  const cleanParams = Object.fromEntries(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Object.entries(params).filter(([_, value]) => value !== undefined),
  );

  return Object.keys(cleanParams).length > 0 ? [...key, cleanParams] : key;
}

export const projectKeys = {
  all: (p?: ProjectKeysAllParmas) => sanitize(['projects', p?.projectId]),

  name: ({ projectId }: ProjectKeysAllParmas) => [
    ...projectKeys.all({ projectId }),
    'name',
  ],

  details: ({ projectId, ...params }: ProjectKeysAllParmas & DetailsParams) =>
    withFilter([...projectKeys.all({ projectId }), 'details'], params),

  notes: ({ projectId, ...params }: ProjectKeysAllParmas & NotesParams) =>
    withFilter([...projectKeys.all({ projectId }), 'notes'], params),

  afterMarket: ({
    projectId,
    ...params
  }: ProjectKeysAllParmas & AfterMarketParams) =>
    withFilter([...projectKeys.all({ projectId }), 'after-market'], params),

  organisations: ({
    projectId,
    ...params
  }: ProjectKeysAllParmas & OrganisationsParams) =>
    withFilter([...projectKeys.all({ projectId }), 'organisations'], params),

  reviewers: ({
    projectId,
    ...params
  }: ProjectKeysAllParmas & ReviewersParams) =>
    withFilter([...projectKeys.all({ projectId }), 'reviewers'], params),

  defaultReviewers: ({
    projectId,
    ...params
  }: ProjectKeysAllParmas & DefaultReviewersParams) =>
    withFilter(
      [...projectKeys.all({ projectId }), 'default-reviewers'],
      params,
    ),

  liftPlan: ({ projectId, ...params }: ProjectKeysAllParmas & LiftPlanParams) =>
    withFilter([...projectKeys.all({ projectId }), 'lift-plan'], params),

  liftPlanUE: ({
    projectId,
    ...params
  }: ProjectKeysAllParmas & LiftPlanUEParams) =>
    withFilter([...projectKeys.all({ projectId }), 'lift-plan-ue'], params),

  debitNorms: ({
    projectId,
    ...params
  }: ProjectKeysAllParmas & DebitNormsParams) =>
    withFilter([...projectKeys.all({ projectId }), 'debit-norms'], params),

  ata: ({ projectId, ...params }: ProjectKeysAllParmas & AtaParams) =>
    withFilter([...projectKeys.all({ projectId }), 'ata'], params),

  ataDetail: ({ projectId, ataId }: ProjectKeysAllParmas & AtaDetailParams) => [
    ...projectKeys.all({ projectId }),
    'ata',
    ataId,
  ],

  ataChangelogs: ({
    projectId,
    ataId,
  }: ProjectKeysAllParmas & AtaDetailParams) => [
    ...projectKeys.all({ projectId }),
    'ata',
    ataId,
    'changelogs',
  ],

  ataDocument: ({
    projectId,
    ataId,
  }: ProjectKeysAllParmas & AtaDetailParams) => [
    'ata-documents',
    projectId,
    ataId,
  ],
};

type AnyFilter = Record<string, unknown>;

type DetailsParams = AnyFilter;
type NotesParams = AnyFilter;
type AfterMarketParams = AnyFilter;
type OrganisationsParams = AnyFilter;
type ReviewersParams = AnyFilter;
type DefaultReviewersParams = AnyFilter;
type LiftPlanParams = AnyFilter;
type LiftPlanUEParams = AnyFilter;
type DebitNormsParams = AnyFilter;
type AtaParams = { status: string } & AnyFilter;
type AtaDetailParams = { ataId: string };
